import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import FieldTextArea from '@atlaskit/field-text-area';
import { Field } from '@atlaskit/form';
import ModalDialog, { ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Select from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { LookingForOption, submitFeedback } from '../clients/feedbackClient';
import { browserMetrics } from '../facade/window';
import FlagStore, { FlagType } from '../store/flagStore';
import Store from '../store/index';
import UserStore from '../store/userStore';

const FEEDBACK_FLAG_ID = 'feedback';

interface CheckboxOnChange {
  target: HTMLInputElement;
}

interface State {
  displayFlag: boolean;
  feedbackText?: string;
  isInvalidText: boolean;
  isOpen: boolean;
  selectedOption: LookingForOption | null;
  userCanBeContacted: boolean;
}

type Props = {
  flagStore: FlagStore;
  userStore: UserStore;
} & InjectedIntlProps;

const CheckboxSeparator = styled.div`
  padding-top: 8px;
`;

const FieldTextFullWidth = styled.div`
  label > div {
    padding-top: 0;
  }

  label + div {
    width: 100%;
  }
`;

const Footer = styled.span`
  display: flex;
  flex: auto;
  justify-content: flex-end;
`;

const FooterButton = styled.span`
  margin-right: 8px;
`;

@observer
class CustomFeedback extends Component<Props, State> {
  static initialState: State = Object.freeze({
    displayFlag: false,
    feedbackText: undefined,
    isInvalidText: false,
    isOpen: false,
    selectedOption: null,
    userCanBeContacted: false,
  });

  state: State = CustomFeedback.initialState;

  close = () => this.setState({ isOpen: false });

  open = () => this.setState({ isOpen: true });

  render() {
    const { intl } = this.props;
    const { feedbackText, isOpen, userCanBeContacted } = this.state;

    return (
      <div>
        <Button appearance="link" data-test-id="feedback" onClick={this.open}>
          <FormattedMessage {...messages.feedbackButton} />
        </Button>
        <ModalTransition>
          {isOpen && (
            <ModalDialog width="medium" components={{ Header: this.header, Footer: this.footer }} onClose={this.close}>
              <Field label={intl.formatMessage(messages.whatAreYouLookingFor)} name="looking-for">
                {() => (
                  <Select
                    menuPortalTarget={document.body}
                    onChange={({ value }: { value: LookingForOption }, { action }: { action: string }) => {
                      if (action !== 'select-option') {
                        return;
                      }
                      this.setState({
                        selectedOption: value,
                      });
                    }}
                    styles={{
                      menuPortal: (base: object) => ({
                        ...base,
                        zIndex: layers.modal(),
                      }),
                    }}
                    options={Object.values(LookingForOption).map((key: LookingForOption) => ({
                      label: optionMessages[key].defaultMessage,
                      value: key,
                    }))}
                    placeholder={intl.formatMessage(messages.chooseAnOption)}
                  />
                )}
              </Field>
              {this.state.selectedOption === LookingForOption.other && (
                <Field label={intl.formatMessage(messages.tellUsMore)} name="looking-for-other">
                  {() => (
                    <FieldTextFullWidth>
                      <FieldTextArea
                        value={feedbackText}
                        compact={true}
                        isInvalid={this.state.isInvalidText}
                        minimumRows={3}
                        onChange={this.handleTextAreaChange}
                        placeholder={intl.formatMessage(messages.placeholder)}
                      />
                    </FieldTextFullWidth>
                  )}
                </Field>
              )}
              <CheckboxSeparator>
                <Checkbox
                  label={intl.formatMessage(messages.canBeContactedLabel)}
                  onChange={this.handleUserCanBeContacted}
                  checked={userCanBeContacted}
                />
              </CheckboxSeparator>
            </ModalDialog>
          )}
        </ModalTransition>
      </div>
    );
  }

  private footer = () => (
    <ModalFooter>
      <Footer>
        <FooterButton>
          <Button appearance="subtle" type="button" onClick={this.close}>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
        </FooterButton>
        <Button appearance="primary" type="submit" onClick={this.submit}>
          <FormattedMessage {...messages.submitButton} />
        </Button>
      </Footer>
    </ModalFooter>
  );

  private getAdditionalFields() {
    return {
      accountId: this.props.userStore.accountId,
      ...browserMetrics(),
    };
  }

  private handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const feedbackText = e.target.value.trim();
    this.setState({
      feedbackText,
      isInvalidText: feedbackText.length === 0,
    });
  };

  private handleUserCanBeContacted = (checkbox: CheckboxOnChange) => {
    this.setState({
      userCanBeContacted: checkbox.target.checked,
    });
  };

  private header = () => (
    <ModalHeader>
      <ModalTitle>
        <FormattedMessage {...messages.dialogHeader} />
      </ModalTitle>
    </ModalHeader>
  );

  private submit = () => {
    const { feedbackText, selectedOption, userCanBeContacted } = this.state;
    const { email, name } = this.props.userStore;

    // User must select an option
    if (!selectedOption) {
      return;
    }

    // User must provide feedback text if they select "other"
    if (selectedOption === LookingForOption.other && (!feedbackText || feedbackText.length === 0)) {
      this.setState({
        isInvalidText: true,
      });
      return;
    }

    void submitFeedback({
      additionalFields: [
        {
          id: 'customfield_11698',
          value: JSON.stringify(this.getAdditionalFields(), null, 2),
        },
      ],
      email: userCanBeContacted ? email : 'do-not-reply@atlassian.com',
      feedbackText,
      lookingFor: selectedOption,
      name,
      userCanBeContacted,
      userInterestedInResearch: false,
    });
    this.setState(CustomFeedback.initialState);

    // optimistically show the flag since we don't wait for the REST call to succeed (it takes a while)
    const { flagStore, intl } = this.props;
    flagStore.addFlag({
      description: intl.formatMessage(messages.flagBody),
      id: FEEDBACK_FLAG_ID,
      title: intl.formatMessage(messages.flagTitle),
      type: FlagType.SUCCESS,
    });
  };
}

export default inject(({ flagStore, userStore }: Store) => {
  return {
    flagStore,
    userStore,
  };
})(injectIntl(CustomFeedback));

const messages = defineMessages({
  canBeContactedLabel: {
    defaultMessage: 'Atlassian can contact me about this feedback',
    description: 'Can be contacted checkbox label',
    id: 'uchi.feedback.can.be.contacted.label',
  },
  cancelButton: {
    defaultMessage: 'Cancel',
    description: 'Close dialog',
    id: 'uchi.feedback.cancel.button',
  },
  chooseAnOption: {
    defaultMessage: 'Choose an option',
    description: 'Placeholder for user to choose an option',
    id: 'uchi.feedback.chooseAnOption',
  },
  dialogHeader: {
    defaultMessage: 'What were you looking for?',
    description: 'Dialog header',
    id: 'uchi.feedback.header',
  },
  feedbackButton: {
    defaultMessage: "Can't find what you're looking for?",
    description: 'Button to launch a dialog to submit feedback',
    id: 'uchi.custom.feedback.button',
  },
  flagBody: {
    defaultMessage: 'Your feedback has been submitted',
    description: 'Body of flag indicating feedback was submitted',
    id: 'uchi.custom.feedback.flagBody',
  },
  flagTitle: {
    defaultMessage: 'Thank you for your feedback!',
    description: 'Title of flag indicating feedback was submitted',
    id: 'uchi.custom.feedback.flagTitle',
  },
  placeholder: {
    defaultMessage: 'A product, an admin panel or something else',
    description: 'Feedback field text area placeholder',
    id: 'uchi.feedback.placeholder',
  },
  researchInterestLabel: {
    defaultMessage: 'I would like to participate in product research',
    description: 'Interested in research checkbox label',
    id: 'uchi.feedback.research.interest.label',
  },
  submitButton: {
    defaultMessage: 'Submit',
    description: 'Submit feedback',
    id: 'uchi.feedback.submit.button',
  },
  tellUsMore: {
    defaultMessage: 'Tell us more',
    description: 'Label for a text field asking for more information',
    id: 'uchi.feedback.tellUsMore',
  },
  whatAreYouLookingFor: {
    defaultMessage: 'Which option best describes what you’re looking for?',
    description: 'Label for a dropdown asking the user to pick what they were looking for',
    id: 'uchi.feedback.whatAreYouLookingFor',
  },
});

const optionMessages = defineMessages<LookingForOption>({
  accessAdmin: {
    defaultMessage: 'Access Admin',
    description: 'option indicating user is trying to access Admin',
    id: 'uchi.lookingFor.accessAdmin',
  },
  accessAnotherProduct: {
    defaultMessage: 'Access another product',
    description: 'option indicating user is trying to access a different product',
    id: 'uchi.lookingFor.accessAnotherProduct',
  },
  accessBitbucket: {
    defaultMessage: 'Access Bitbucket',
    description: 'option indicating user is trying to access Bitbucket',
    id: 'uchi.lookingFor.accessBitbucket',
  },
  accessConfluence: {
    defaultMessage: 'Access Confluence',
    description: 'option indicating user is trying to access Confluence',
    id: 'uchi.lookingFor.accessConfluence',
  },
  accessJira: {
    defaultMessage: 'Access Jira',
    description: 'option indicating user is trying to access Jira',
    id: 'uchi.lookingFor.accessJira',
  },
  askAQuestionOnCommunity: {
    defaultMessage: 'Ask a question on Community',
    description: 'option indicating user is trying to ask a question on Community',
    id: 'uchi.lookingFor.askAQuestionOnCommunity',
  },
  buyAnAtlassianProduct: {
    defaultMessage: 'Buy an Atlassian product',
    description: 'option indicating user is trying to buy an Atlassian product',
    id: 'uchi.lookingFor.buyAnAtlassianProduct',
  },
  other: {
    defaultMessage: 'Other',
    description:
      'option indicating user trying to choose a different option (and provide details in a free text field)',
    id: 'uchi.lookingFor.other',
  },
  startATrialOfAnAtlassianProduct: {
    defaultMessage: 'Start a trial of an Atlassian product',
    description: 'option indicating user is trying to start a trial of an Atlassian product',
    id: 'uchi.lookingFor.startATrialOfAnAtlassianProduct',
  },
  talkToSupport: {
    defaultMessage: 'Talk to support',
    description: 'option indicating user is trying to talk to support',
    id: 'uchi.lookingFor.talkToSupport',
  },
  updateMyProfileDataOrPassword: {
    defaultMessage: 'Update my profile data or password',
    description: 'option indicating user is trying to update their profile data or password',
    id: 'uchi.lookingFor.updateMyProfileDataOrPassword',
  },
});
